import { useState, useEffect, useCallback } from 'react'
import { getTags, createTag, updateTag, deleteTag, Tag } from '../api/tags'

interface TagsManagerProps {
  onClose?: () => void
  onTagsChange?: () => void
}

export default function TagsManager({ onClose, onTagsChange }: TagsManagerProps) {
  const [tags, setTags] = useState<Tag[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [newTagName, setNewTagName] = useState('')
  const [editingTagId, setEditingTagId] = useState<string | null>(null)
  const [editingTagName, setEditingTagName] = useState('')
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const fetchTags = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const fetchedTags = await getTags()
      const sortedTags = [...fetchedTags].sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      )
      setTags(sortedTags)
    } catch (err) {
      setError('Failed to load tags')
      console.error('Error fetching tags:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTags()
  }, [fetchTags])

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return
    if (newTagName.length > 254) {
      setError('Tag name is too long (max 254 characters)')
      return
    }
    try {
      setSaving(true)
      setError(null)
      await createTag({ name: newTagName.trim() })
      setNewTagName('')
      await fetchTags()
      onTagsChange?.()
    } catch (err) {
      setError('Failed to create tag')
      console.error('Error creating tag:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleUpdateTag = async (tagId: string) => {
    if (!editingTagName.trim()) return
    if (editingTagName.length > 254) {
      setError('Tag name is too long (max 254 characters)')
      return
    }
    try {
      setSaving(true)
      setError(null)
      await updateTag({ id: tagId, name: editingTagName.trim() })
      setEditingTagId(null)
      setEditingTagName('')
      await fetchTags()
      onTagsChange?.()
    } catch (err) {
      setError('Failed to update tag')
      console.error('Error updating tag:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteTag = async (tagId: string) => {
    try {
      setSaving(true)
      setError(null)
      await deleteTag(tagId)
      setDeleteConfirmId(null)
      await fetchTags()
      onTagsChange?.()
    } catch (err) {
      setError('Failed to delete tag')
      console.error('Error deleting tag:', err)
    } finally {
      setSaving(false)
    }
  }

  const startEditing = (tag: Tag) => {
    setEditingTagId(tag.id)
    setEditingTagName(tag.name)
    setDeleteConfirmId(null)
  }

  const cancelEditing = () => {
    setEditingTagId(null)
    setEditingTagName('')
  }

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      action()
    } else if (e.key === 'Escape') {
      cancelEditing()
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
          </svg>
          <h2 className="text-lg font-semibold text-gray-900">Manage Tags</h2>
        </div>
        {onClose && (
          <button onClick={onClose} data-testid="tags-manager-close" className="text-gray-600 hover:text-gray-900 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="mx-4 mt-4 p-3 bg-red-100 border border-red-300 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        {/* Tags list */}
        <div className="max-h-64 overflow-y-auto mb-4 space-y-2">
          {loading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : tags.length === 0 ? (
            <p className="text-gray-600 text-center py-4">No tags yet. Create one below.</p>
          ) : (
            tags.map((tag) => (
              <TagRow
                key={tag.id}
                tag={tag}
                editingTagId={editingTagId}
                editingTagName={editingTagName}
                deleteConfirmId={deleteConfirmId}
                saving={saving}
                onEditingNameChange={setEditingTagName}
                onStartEditing={startEditing}
                onCancelEditing={cancelEditing}
                onUpdate={handleUpdateTag}
                onDelete={handleDeleteTag}
                onDeleteConfirm={setDeleteConfirmId}
                onKeyDown={handleKeyDown}
              />
            ))
          )}
        </div>

        {/* Add new tag */}
        <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
          <input
            type="text"
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, handleCreateTag)}
            placeholder="New tag name..."
            data-testid="new-tag-input"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
            disabled={saving}
            maxLength={254}
          />
          <button
            onClick={handleCreateTag}
            disabled={saving || !newTagName.trim()}
            data-testid="add-tag-btn"
            className="p-2 bg-primary text-white rounded-lg hover:bg-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Add tag"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}

interface TagRowProps {
  tag: Tag
  editingTagId: string | null
  editingTagName: string
  deleteConfirmId: string | null
  saving: boolean
  onEditingNameChange: (name: string) => void
  onStartEditing: (tag: Tag) => void
  onCancelEditing: () => void
  onUpdate: (tagId: string) => void
  onDelete: (tagId: string) => void
  onDeleteConfirm: (tagId: string | null) => void
  onKeyDown: (e: React.KeyboardEvent, action: () => void) => void
}

function TagRow({
  tag, editingTagId, editingTagName, deleteConfirmId, saving,
  onEditingNameChange, onStartEditing, onCancelEditing, onUpdate, onDelete, onDeleteConfirm, onKeyDown
}: TagRowProps) {
  if (editingTagId === tag.id) {
    return (
      <div data-testid="tag-row" className="flex items-center gap-2">
        <input
          type="text"
          value={editingTagName}
          onChange={(e) => onEditingNameChange(e.target.value)}
          onKeyDown={(e) => onKeyDown(e, () => onUpdate(tag.id))}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
          autoFocus
          disabled={saving}
        />
        <button onClick={() => onUpdate(tag.id)} disabled={saving || !editingTagName.trim()} className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors disabled:opacity-50" title="Save">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
        </button>
        <button onClick={onCancelEditing} disabled={saving} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50" title="Cancel">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>
    )
  }

  if (deleteConfirmId === tag.id) {
    return (
      <div data-testid="tag-row" className="flex items-center gap-2">
        <span className="flex-1 text-sm text-red-600">Delete "{tag.name}"?</span>
        <button onClick={() => onDelete(tag.id)} disabled={saving} className="px-3 py-1 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50">Delete</button>
        <button onClick={() => onDeleteConfirm(null)} disabled={saving} className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50">Cancel</button>
      </div>
    )
  }

  return (
    <div data-testid="tag-row" className="flex items-center gap-2">
      <span className="flex-1 px-3 py-2 text-gray-900 text-sm truncate">{tag.name}</span>
      <button onClick={() => onStartEditing(tag)} data-testid="tag-edit-btn" className="p-2 text-gray-600 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors" title="Edit">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
      </button>
      <button onClick={() => onDeleteConfirm(tag.id)} data-testid="tag-delete-btn" className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
      </button>
    </div>
  )
}
